import React, { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/v2/components/DataTable/tasks/components/data-table';
import { DataTableColumnHeader } from '@/v2/components/DataTable/tasks/components/data-table-column-header';
import useFetch from 'use-http';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  useUserDispatch,
  useUserState,
} from '@/components/context/UserContext';
import { getQueryString } from '@/components/constants/visitsQueryStringBuilder';
import { InfoButton } from '@/components/ui/shared/InfoButton';

export default function PathologyResearchesTable() {
  const { t } = useTranslation();
  const history = useHistory();
  const sessionDispatch = useUserDispatch();
  const userState = useUserState();

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState(userState.dateFilter);

  const { get: getResearches, response: researchesResp } = useFetch(
    '/UserAccount/PathologyResearches',
  );
  const { get: getResearch } = useFetch('/UserAccount/PathologyResearch');

  const fetchData = async (
    date = dateRange,
    term = search,
    filter = selectedFilter,
  ) => {
    setIsLoading(true);

    sessionDispatch({ type: 'UPDATE', data: { dateFilter: date } });

    const res = await getResearches(
      getQueryString(date, term, null, filter, userState.espbiEsi),
    );

    if (researchesResp.ok) {
      const list = res?.reaserches ?? [];
      const sorted = _.orderBy(list, ['orderDate'], ['desc']);

      setData(sorted);

      const opts =
        res?.filters?.map((f) => ({ value: f.id, label: f.displayName })) ?? [];

      setFilters(opts);
    }

    setIsLoading(false);
  };

  const handleSelection = async (id) => {
    setIsLoading(true);

    const query = userState.espbiEsi
      ? `?id=${id}&espbiEsi=${userState.espbiEsi}`
      : `?id=${id}`;

    const res = await getResearch(query);

    history.push('/userAccount/pathology-researches/details', { data: res });

    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [dateRange, userState?.espbiEsi]);

  return (
    <DataTable
      title="Patologiniai tyrimai"
      loading={isLoading}
      columns={columns({ handleSelection })}
      data={data}
      filterableColumns={['name', 'status.displayName', 'orderDate']}
      onRowClick={(row) => handleSelection(row.id)}
    />
  );
}

const capitalizeFirstLetter = (text) => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const formatDateWithoutConversion = (date) => {
  if (!date) {
    return '-';
  }

  const value = String(date);
  const datePart = value.substring(0, 10);
  const timePart = value.substring(11, 16);

  if (!datePart) {
    return '-';
  }

  if (!timePart || timePart === '00:00') {
    return datePart;
  }

  return `${datePart} ${timePart}`;
};

const columns = ({ handleSelection }) => [
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="TYRIMAS" />
    ),
    cell: ({ row }) => (
      <span className="font-medium max-w-[400px] truncate ">
        {capitalizeFirstLetter(row.original.name)}
      </span>
    ),
  },
  {
    accessorKey: 'orderDate',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="DATA" />
    ),
    cell: ({ row }) => (
      <span className="text-muted-foreground">
        {formatDateWithoutConversion(row.original.orderDate)}
      </span>
    ),
  },
  {
    accessorKey: 'status.displayName',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="BŪSENA" />
    ),
    cell: ({ row }) => (
      <Badge variant="neutral">{row.original.status.displayName}</Badge>
    ),
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => (
      <div className="flex justify-end">
        <InfoButton
          onClick={(e) => {
            e.stopPropagation();
            handleSelection(row.original.id);
          }}
        >
          Informacija
        </InfoButton>
      </div>
    ),
  },
];